'use client';

import { XCircle, Eye } from 'lucide-react';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';

interface ViewVisualizationOutput {
  success: boolean;
  visualizationId?: string;
  type?: string;
  title?: string;
  imageUrl?: string;
  error?: string;
}

/**
 * Normalize output — during live streaming it's { success, title, ... } from execute(),
 * but when reloaded from DB it may be the toModelOutput array format:
 * [{ type: 'text', text: 'Rendered visualization "Title" ...' }, { type: 'image-data', ... }]
 */
function normalizeOutput(output: unknown): ViewVisualizationOutput {
  if (output && typeof output === 'object' && 'success' in output) {
    return output as ViewVisualizationOutput;
  }

  // Array format from toModelOutput — extract info from the text content
  if (Array.isArray(output)) {
    const textPart = output.find((p: { type?: string }) => p.type === 'text');
    if (textPart?.text) {
      const titleMatch = (textPart.text as string).match(/Rendered visualization "([^"]+)"/);
      return {
        success: true,
        title: titleMatch?.[1],
      };
    }
  }

  return { success: false, error: 'Unknown output format' };
}

/**
 * Custom UI for the viewVisualization tool result.
 * Shows a compact status indicator — success (green check) or failure (red X).
 */
export function ViewVisualizationToolOutput({ output: rawOutput }: { output: unknown }) {
  const output = normalizeOutput(rawOutput);
  useProductTelemetryOnce(
    'view_visualization_failed',
    !output.success,
    {
      failure_kind: 'view-visualization-failed',
    },
    output.success ? undefined : output.error,
  );

  if (!output.success) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/50">
        <XCircle className="h-3 w-3 shrink-0" />
        view failed{output.error ? ` — ${output.error}` : ''}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/50">
      <Eye className="h-3 w-3 shrink-0" />
      viewed{output.title ? ` "${output.title}"` : ''}
    </span>
  );
}
